import React, { useState, useEffect } from 'react';
import { HERO_IMAGES as FALLBACK_IMAGES } from '../data/galleryImages';
import { AWS_CONFIG } from '../config/aws';
import { NAVER_BOOKING_URL, NAVER_BLOG_URL } from '../data/siteConfig';
import UsedCarInquiryModal from './UsedCarInquiryModal';
import useIsMobile from '../utils/useIsMobile';

export default function HeroBanner() {
  const [images, setImages] = useState(FALLBACK_IMAGES);
  const [activeIdx, setActiveIdx] = useState(0);
  const [usedCarOpen, setUsedCarOpen] = useState(false);
  const isMobile = useIsMobile();

  // 워터마크 밴드는 배경 이미지 '원본 비율'(1536x1024 ≈ 3:2)로 그려 흰 배경이
  // 세로로 잘리지 않게 한다(2026-07-02, 사장님 요청: 흰 배경 살림 + 워터마크 바로
  // 아래 텍스트). 폭에 비례하므로 모든 기종에서 동일한 모습(반응형 자동). 다만 넓은
  // PC에선 밴드가 과하게 커지므로 maxHeight로만 캡한다. 값↑ = 흰 배경/워터마크가 커짐.
  const heroBandMaxH = isMobile ? '300px' : '460px';

  useEffect(() => {
    fetch(`${AWS_CONFIG.apiUrl}/images?category=hero`)
      .then(r => r.json())
      .then(data => {
        if (data.images?.length > 0) {
          setImages(data.images.map(i => i.imageUrl));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  // Shared style for the two Naver CTA buttons. width:100% + the fit-content
  // flex wrapper makes both buttons the same box size regardless of label length
  // (the longer "네이버 예약 하기" defines the width; the blog button stretches to match).
  const naverBtnStyle = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
    padding: '18px 44px',
    fontSize: 'clamp(1.15rem, 3vw, 1.4rem)',
    fontWeight: 700,
    background: '#03C75A',
    color: '#fff',
    border: '2px solid #fff',
    borderRadius: 12,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
  };

  return (
    <>
    <section style={{
      position: 'relative', minHeight: '70vh',
      margin: 'clamp(12px, 2vw, 24px)',
      // nav(64px) 공간만 상단에 두고, 그 아래에 워터마크 밴드 → 전경 텍스트 순서로
      // flow 배치(2026-07-02). 밴드가 원본 비율이라 흰 배경이 자연스럽게 다 살고,
      // 전경 텍스트는 워터마크 '바로 아래'에 붙는다. 모든 기종 동일(반응형 자동).
      paddingTop: 64,
      paddingBottom: 'clamp(40px, 8vh, 96px)',
      borderRadius: 'clamp(12px, 2vw, 16px)',
      overflow: 'hidden', backgroundColor: '#0A1628',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      {/* 워터마크 밴드: 배경 이미지를 '원본 비율(3:2)'로 담아 흰 배경 세로 잘림을
          없앤다. 여러 hero 이미지는 이 안에서 crossfade. maxHeight로만 넓은 PC의
          과대 확대를 캡한다(캡 시에만 cover로 세로 살짝 crop, 워터마크는 중앙 유지). */}
      <div style={{
        position: 'relative', width: '100%',
        aspectRatio: '1536 / 1024',
        maxHeight: heroBandMaxH,
        overflow: 'hidden',
        borderRadius: 'clamp(8px, 1.5vw, 12px)',
      }}>
        {images.map((url, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            // Quote the URL so filenames with parens/spaces (e.g. "3316(1).jpeg") don't
            // break CSS url() parsing; escape any embedded double-quote defensively.
            backgroundImage: `url("${url.replace(/"/g, '%22')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: i === activeIdx ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
          }} />
        ))}
        {/* 밴드 하단 fade: 밝은 배경 → 남색으로 자연 전환(카드처럼 뜨는 경계 제거) */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: '32%',
          background: 'linear-gradient(to bottom, rgba(10,22,40,0) 0%, #0A1628 100%)',
        }} />
      </div>

      {/* Text content — 워터마크 밴드 바로 아래(flow). paddingTop만 살짝 줘 근접 배치. */}
      <div style={{
        position: 'relative', zIndex: 2,
        animation: 'fadeUp 0.8s ease-out',
        padding: 'clamp(20px, 4vh, 44px) 20px 0', textAlign: 'center',
      }}>
        <p style={{
          color: 'var(--accent)', fontSize: '1.1rem',
          fontWeight: 600, marginBottom: 16,
          letterSpacing: 2, textTransform: 'uppercase',
        }}>
          Premium Steam Hand Wash
        </p>
        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 800, lineHeight: 1.2,
          marginBottom: 20, textShadow: '0 2px 12px rgba(0,0,0,0.5)',
          // keep-all: 글자 중간이 아니라 단어(공백) 단위로만 줄바꿈(2026-07-01).
          wordBreak: 'keep-all',
        }}>
          깨끗함을 넘어
          <br />
          {/* 모바일은 '차량의 가치를' / '관리합니다'를 항상 3줄로 고정(2026-07-02,
              사장님 확정). 폰 폭마다 2·3줄로 들쭉날쭉하던 문제 해소. PC는 넓어
              공백으로 이어 자연스러운 2줄 유지. */}
          <span style={{ color: 'var(--primary)' }}>차량의 가치</span>를
          {isMobile ? <br /> : ' '}
          관리합니다
        </h1>
        <p style={{
          color: 'var(--text)', fontSize: '1.15rem',
          maxWidth: 480, margin: '0 auto 32px',
          textShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }}>
          스팀은 옵션이 아닙니다.
          <br />
          <strong style={{ color: 'var(--primary)' }}>모든 세차에 스팀이 기본</strong>입니다.
        </p>
        {/* Two Naver CTAs share one fit-content flex column so both render at the
            same box width (2026-06-01). 예약·블로그 모두 동일 크기·네이버 그린(#03C75A). */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'stretch', gap: 14,
          width: 'fit-content', margin: '0 auto',
        }}>
          <a
            href={NAVER_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
            style={naverBtnStyle}
          >
            네이버 예약 하기 →
          </a>
          <a
            href={NAVER_BLOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
            style={naverBtnStyle}
          >
            네이버 블로그 →
          </a>
          <button
            type="button"
            onClick={() => setUsedCarOpen(true)}
            className="btn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', boxSizing: 'border-box',
              padding: '18px 44px',
              fontSize: 'clamp(1.15rem, 3vw, 1.4rem)', fontWeight: 700,
              background: '#1E5FBF', color: '#fff',
              border: '2px solid #fff', borderRadius: 12,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)', cursor: 'pointer',
            }}
          >
            {/* Inline car icon — replaces the previous emoji for consistent
                cross-device rendering; inherits white via currentColor. */}
            <svg
              width="1.35em" height="1.35em" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}
            >
              <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
              <circle cx="7" cy="17" r="2" />
              <path d="M9 17h6" />
              <circle cx="17" cy="17" r="2" />
            </svg>
            중고차 구매
          </button>
        </div>
      </div>

      {/* Slide indicator dots — 모바일에서는 숨김. 전경 CTA 블록을 하단 정렬한 뒤
          우하단 인디케이터(파란 막대)가 '중고차 구매' 버튼 옆에 겹쳐 하이픈처럼
          보이던 문제 해소(2026-07-01). 데스크톱은 코너에 여백이 있어 유지. */}
      {!isMobile && (
      <div style={{
        position: 'absolute', bottom: 32, right: 32, zIndex: 2,
        display: 'flex', gap: 8,
      }}>
        {images.map((_, i) => (
          <div key={i}
            onClick={() => setActiveIdx(i)}
            style={{
              width: 24, height: 3, cursor: 'pointer',
              background: i === activeIdx
                ? 'var(--primary)'
                : 'rgba(255,255,255,0.3)',
              transition: 'background 0.3s',
              borderRadius: 2,
            }}
          />
        ))}
      </div>
      )}
    </section>
    <UsedCarInquiryModal open={usedCarOpen} onClose={() => setUsedCarOpen(false)} />
    </>
  );
}
